import React, { useEffect, useState } from 'react';
import { Button } from '@material-ui/core';
import { InfoOutlined } from '@material-ui/icons';
import { Trans, t } from '@lingui/macro';
import { FormProvider, useForm } from 'react-hook-form';
import { isNil, isString } from 'lodash';
import moment from 'moment';

import { buildPageId, useEntityIdParam } from '../AdminLinkUtils';
import AdminPage from '../AdminPage';
import { AdminPages } from '../AdminPages';
import { USE_FORM_DEFAULT_OPTIONS } from '../../../utilities/FormUtilities';
import NgpForm from '../../form/NgpForm';
import FormSection from '../../form/FormSection';
import NgpTextField from '../../form/NgpTextField';
import NgpTextarea from '../../form/NgpTextarea';
import NgpDateTime from '../../form/NgpDateTime';
import { useFormSaveService, useFormSubmitWrapper } from '../../../services/FormSaveService';
import { useFindById } from '../../../services/FormQueryService';
import { useFormDeleteService, useFormDeleteWrapper } from '../../../services/FormDeleteService';
import { MaintenanceWindowsSitesSection } from './MaintenanceWindowsSitesSection';
import { MaintenanceWindowsBusinessServicesSection } from './MaintenanceWindowsBusinessServicesSection';
import useBreadcrumb from '../../breadcrumb/UseBreadcrumb';
import { Actions } from '../../page-framework';
import ConfirmDeleteButton from '../../form/ConfirmDeleteButton';
import {
    isValidEndTimeAfterStartTime,
    isValidFutureDateTime,
    useMaintenanceWindowFormStyles,
} from './MaintenanceWindowsUtils';
import {
    MAINTENANCE_WINDOW_DESCRIPTION_FIELD,
    MAINTENANCE_WINDOW_END_TIME_FIELD,
    MAINTENANCE_WINDOW_NAME_FIELD,
    MAINTENANCE_WINDOW_START_TIME_FIELD,
} from './MaintenanceWindowsConstants';
import { useDataService } from '../../../services/ApiDataService';
import { useTimezone } from '../../../stores/SessionStore';

const functionName = AdminPages.MaintenanceWindows;
const newBreadCrumbText = t`New Maintenance Window`;
const goToUrl = '../';
const defaultFormValues = {};

export default function MaintenanceWindowsDetails() {
    const styles = useMaintenanceWindowFormStyles();
    const [asyncLoading, setAsyncLoading] = useState(true);
    const [maintenanceWindowNames, setMaintenanceWindowNames] = useState([]);
    const [originalName, setOriginalName] = useState(undefined);
    const [showDeleteButton, setShowDeleteButton] = useState(false);
    const [sitesSelectionDirty, setSitesSelectionDirty] = useState(false);
    const [businessServicesSelectionDirty, setBusinessServicesSelectionDirty] = useState(false);
    const methods = useForm(USE_FORM_DEFAULT_OPTIONS);
    const { reset, getValues, trigger, setError, handleSubmit } = methods;
    const { submitWrapper, saving } = useFormSubmitWrapper();
    const { deleteWrapper, deleting } = useFormDeleteWrapper();
    const formSaveService = useFormSaveService();
    const recordId = useEntityIdParam();
    const functionBreadCrumbs = [{ link: goToUrl, text: t`Maintenance Windows` }];
    const formDeleteService = useFormDeleteService();
    const dataService = useDataService();
    const [tz] = useTimezone();

    useEffect(() => {
        (async () => {
            const windows = await dataService.get('/api/v3/maintenance-windows/list');
            setMaintenanceWindowNames(windows.data.map((window) => window.name.toLowerCase()));
            setAsyncLoading(false);
        })();

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const { initialized, finding, formData } = useFindById({
        isV3Api: true,
        url: '/api/v3/maintenance-windows/',
        recordId,
        postFindByIdHandler: (maintenanceWindow) => {
            setOriginalName(maintenanceWindow?.data?.name?.toLowerCase());
            setShowDeleteButton(true);

            return maintenanceWindow;
        },
        resetFunc: reset,
        defaultFormValues,
    });

    // Disable all fields on past maintenance windows as they are non-editable
    let fieldsDisabled = false;
    if (new Date(formData.endTime).getTime() < new Date().getTime()) {
        fieldsDisabled = true;
    }

    // Disable start time if it has already passed
    let startTimeDisabled = false;
    if (!fieldsDisabled && new Date(formData.startTime).getTime() < new Date().getTime()) {
        startTimeDisabled = true;
    }

    const breadcrumbs = useBreadcrumb(functionBreadCrumbs, recordId, formData, 'name', newBreadCrumbText);

    const handleFormSubmit = (submitData) => {
        const dataToSave = {
            ...formData,
            ...submitData,
            sites: submitData.sites?.map((siteId) => (isString(siteId) ? { id: siteId } : siteId)) || [],
            businessServices: submitData.businessServices?.map((bsId) => (isString(bsId) ? { id: bsId } : bsId)) || [],
            startTime: moment.isMoment(submitData.startTime)
                ? submitData.startTime.tz(tz).toISOString()
                : submitData.startTime,
            endTime: moment.isMoment(submitData.endTime) ? submitData.endTime.tz(tz).toISOString() : submitData.endTime,
        };

        return formSaveService.save({
            isV3Api: true,
            url: '/api/v3/maintenance-windows',
            recordId,
            dataToSave,
            setError,
        });
    };
    const handleSubmitFunction = submitWrapper(handleFormSubmit, goToUrl);
    const dirtyCheck = (isDirty) => isDirty || sitesSelectionDirty || businessServicesSelectionDirty;
    const handleConfirmDelete = () => {
        return deleteWrapper({
            deleteFunction: () =>
                formDeleteService.deleteById('/api/v3/maintenance-windows', recordId, (record) => record, true),
            onDeleteGoTo: goToUrl,
        })();
    };

    return (
        <AdminPage
            pageId={buildPageId(functionName)}
            breadcrumbs={breadcrumbs}
            actions={
                <Actions>
                    {showDeleteButton && (
                        <ConfirmDeleteButton
                            handleConfirmedDelete={handleConfirmDelete}
                            plural={false}
                            disabled={fieldsDisabled || startTimeDisabled}
                        />
                    )}
                    <Button
                        onClick={handleSubmit(handleSubmitFunction)}
                        variant="contained"
                        color="primary"
                        disabled={fieldsDisabled}
                    >
                        <Trans>Save</Trans>
                    </Button>
                </Actions>
            }
        >
            <FormProvider {...methods}>
                <NgpForm
                    submitFunction={handleSubmitFunction}
                    initialized={initialized}
                    loading={finding || saving || deleting || asyncLoading}
                    isDirtyFunc={dirtyCheck}
                    showSave={false}
                    focusFirstField={false}
                >
                    {fieldsDisabled && (
                        <div className={styles.detailsInfoWrapper}>
                            <InfoOutlined className={styles.detailsInfoIcon} />
                            <Trans>
                                Maintenance windows that have already completed in the past cannot be edited&#46;
                            </Trans>
                        </div>
                    )}
                    <FormSection title={t`Maintenance Window Detail`}>
                        <NgpTextField
                            fieldName={MAINTENANCE_WINDOW_NAME_FIELD}
                            label={t`Name`}
                            required
                            inputProps={{ size: 40, maxLength: 128 }}
                            disabled={fieldsDisabled}
                            fieldRules={{
                                validate: {
                                    validUniqueName: (value) => {
                                        value = value.toLowerCase();

                                        if (value !== originalName) {
                                            return (
                                                !maintenanceWindowNames.includes(value) ||
                                                t`Maintenance window name must be unique.`
                                            );
                                        }

                                        return true;
                                    },
                                },
                            }}
                        />
                        <NgpTextarea
                            fieldName={MAINTENANCE_WINDOW_DESCRIPTION_FIELD}
                            label={t`Description`}
                            cols={50}
                            inputProps={{ maxLength: 225 }}
                            disabled={fieldsDisabled}
                        />
                    </FormSection>
                    <FormSection title={t`Maintenance Window Start/End Times`}>
                        {startTimeDisabled && (
                            <div className={styles.fieldInfoWrapper}>
                                <InfoOutlined className={styles.detailsInfoIcon} />
                                <Trans>
                                    Maintenance window has already started&#44; the start time cannot be changed&#46;
                                </Trans>
                            </div>
                        )}
                        <NgpDateTime
                            fieldName={MAINTENANCE_WINDOW_START_TIME_FIELD}
                            dateLabel={t`Start Date`}
                            timeLabel={t`Start Time`}
                            required
                            disabled={fieldsDisabled || startTimeDisabled}
                            onChangeFunc={() => !isNil(getValues('endTime')) && trigger('endTime')}
                            fieldRules={{
                                validFutureDateTime: (value) =>
                                    startTimeDisabled
                                        ? true
                                        : isValidFutureDateTime(value, 'Start time must be in the future.', tz),
                            }}
                        />
                        <NgpDateTime
                            fieldName={MAINTENANCE_WINDOW_END_TIME_FIELD}
                            dateLabel={t`End Date`}
                            timeLabel={t`End Time`}
                            required
                            disabled={fieldsDisabled}
                            onChangeFunc={() => !isNil(getValues('startTime')) && trigger('startTime')}
                            fieldRules={{
                                validFutureDateTime: (value) =>
                                    isValidFutureDateTime(value, 'End time must be in the future.', tz),
                                validAfterStartTime: (value) => {
                                    const startTime = getValues('startTime');
                                    if (startTime) {
                                        return isValidEndTimeAfterStartTime(
                                            startTime,
                                            value,
                                            'The end time must be after the start time.',
                                            tz
                                        );
                                    }

                                    return true;
                                },
                            }}
                        />
                    </FormSection>
                    <MaintenanceWindowsBusinessServicesSection
                        formData={formData}
                        setBusinessServicesSelectionDirty={setBusinessServicesSelectionDirty}
                        disabled={fieldsDisabled}
                    />
                    <MaintenanceWindowsSitesSection
                        formData={formData}
                        setSitesSelectionDirty={setSitesSelectionDirty}
                        disabled={fieldsDisabled}
                    />
                </NgpForm>
            </FormProvider>
        </AdminPage>
    );
}
